/**
 * Request Demo Service for Azure Static Web Apps
 * Handles demo request processing and sales team notifications using serverless Azure Functions
 */

// Azure Static Web App API Configuration
const AZURE_API_CONFIG = {
  // Azure Static Web App API endpoint
  apiUrl: process.env.REACT_APP_API_URL || '/api',
  // Fallback for local development
  localApiUrl: process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:7071/api'
};

// Get the appropriate API URL based on environment
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || 
                     hostname === '127.0.0.1' || 
                     hostname.includes('localhost') ||
                     hostname.includes('192.168.') ||
                     hostname.includes('10.0.') ||
                     hostname.includes('172.');
  
  // Check if we're in production (not localhost)
  if (!isLocalhost) {
    return AZURE_API_CONFIG.apiUrl;
  }
  // Local development
  return AZURE_API_CONFIG.localApiUrl;
};

/**
 * Get access token for Request Demo via Azure Function
 * @returns {Promise<string>} Access token
 */
export const getAccessToken = async () => {
  try {
    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/zoho-token`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Token request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.access_token;
  } catch (error) {
    throw new Error('Failed to get Request Demo access token');
  }
};

/**
 * Create a new lead for Request Demo via Azure Function
 * @param {Object} leadData - Lead information
 * @returns {Promise<Object>} Creation result
 */
export const createLead = async (leadData) => {
  try {
    const apiUrl = getApiUrl();
    
    // Prepare lead data for Zoho CRM
    const zohoLeadData = {
      First_Name: leadData.name.split(' ')[0] || leadData.name,
      Last_Name: leadData.name.split(' ').slice(1).join(' ') || '',
      Email: leadData.email,
      Phone: leadData.phone || '',
      Company: leadData.company || 'Not specified',
      Lead_Source: 'Website Demo Request',
      Description: `Demo Request Details:
        - Fleet Size: ${leadData.fleetSize || 'Not specified'}
        - Notes: ${leadData.notes || 'No additional notes'}
        - Domain: ${leadData.domain || 'Unknown'}
        - Submitted: ${leadData.createdOn || new Date().toISOString()}`,
      Lead_Status: 'Not Contacted',
      Rating: 'Hot'
    };

    // Create lead in Zoho CRM via Azure Function
    const response = await fetch(`${apiUrl}/zoho-leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(zohoLeadData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Lead creation failed: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
    }

    const result = await response.json();
    
    return {
      success: true,
      leadId: result.leadId,
      message: 'Demo request submitted successfully'
    };

  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: 'Failed to submit demo request'
    };
  }
};

/**
 * Create a task for sales team follow-up using Azure Function
 * @param {Object} taskData - Task information
 * @returns {Promise<Object>} Task creation result
 */
export const createSalesTask = async (taskData) => {
  try {
    const apiUrl = getApiUrl();
    
    const response = await fetch(`${apiUrl}/tasks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        leadName: taskData.leadName,
        leadEmail: taskData.leadEmail,
        company: taskData.company,
        phone: taskData.phone,
        fleetSize: taskData.fleetSize,
        notes: taskData.notes,
        domain: taskData.domain
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Task creation failed: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`);
    }

    const result = await response.json();
    
    return {
      success: result.success,
      taskId: result.taskId,
      message: result.message || 'Demo request processed successfully'
    };

  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: 'Failed to process demo request'
    };
  }
};

/**
 * Complete demo request processing - creates task for sales team
 * @param {Object} formData - Form data from demo request
 * @returns {Promise<Object>} Complete process result
 */
export const processDemoRequest = async (formData) => {
  try {
    // Add domain information
    const taskData = {
      ...formData,
      domain: window.location.hostname || 'localhost'
    };

    // Create task for sales team (no lead creation)
    const taskResult = await createSalesTask({
      leadName: formData.name,
      leadEmail: formData.email,
      company: formData.company,
      fleetSize: formData.fleetSize,
      notes: formData.notes,
      phone: formData.phone,
      domain: taskData.domain
    });

    if (!taskResult.success) {
      return taskResult;
    }

    return {
      success: true,
      taskId: taskResult.taskId,
      message: 'Demo request submitted successfully. Our team will contact you soon.'
    };

  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: 'Failed to process demo request'
    };
  }
};

/**
 * Test Zoho CRM connection via Azure Function
 * @returns {Promise<Object>} Connection test result
 */
export const testZohoConnection = async () => {
  try {
    const apiUrl = getApiUrl();
    
    // Test connection via Azure Function
    const response = await fetch(`${apiUrl}/zoho-test`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Connection test failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return {
      success: result.success,
      message: result.message || 'Demo request system connection successful',
      apiUrl: apiUrl
    };

  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: 'Demo request system connection failed',
      apiUrl: getApiUrl()
    };
  }
};
